import oneStopLeaveValidation from 'domain/people/leave/createupdate/oneStopLeaveValidation';
import leaveValidation from 'domain/people/leave/createupdate/leaveValidation';
import leaveFilterValidation from 'domain/people/leave/List/filter/leaveFilterValidation';
import { useBusinessEngineApi, usePeopleManagementApi } from 'infrastructure/api';
import { useCrudService } from 'framework/crud';

import { useAuthorization } from 'framework/authorization';
import { CREATE_EXPENSE, FIND_EMPLOYEE_EXPENSES_BY_LEAVE_IDS, useExpenseService } from 'domain/shared/expense';

import {
  LEAVE_TYPES_ALIASES,
  LEAVE_STATUSES_ALIASES,
  LeaveDto,
  LeaveDtoWithCompensation,
  LeaveFormData,
} from 'domain/shared/leave';
import { Data } from 'framework/types';
import { mapLeaveFormDataToCreateLeaveCommand, mapLeaveFormDataToUpdateLeaveCommand } from './leaveMappers';
import { assertMaternityLeaveAllowed } from './maternityLeaveEligibility';
import { FormFieldsData } from 'framework/form';
import { ListData } from 'framework/list';
import { ShowData } from 'framework/show';
import { useEmployeeService } from 'domain/shared/employee';

export const hasSignatureOption = (leave: FormFieldsData) => {
  const typesWithSignatureOption = new Set<string>([
    LEAVE_TYPES_ALIASES.PAID_LEAVE,
    LEAVE_TYPES_ALIASES.UNPAID_LEAVE,
    LEAVE_TYPES_ALIASES.SICK_DAY,
    LEAVE_TYPES_ALIASES.LEAVE_FOR_PERSONAL_CIRCUMSTANCES,
    LEAVE_TYPES_ALIASES.MATERNITY_LEAVE,
    'COMPENSATION',
  ]);
  if (leave instanceof ShowData) {
    const t = leave.getValueBySource('type');
    return t != null && typesWithSignatureOption.has(t);
  }
  return leave.type != null && typesWithSignatureOption.has(leave.type);
};

const useLeaveService = () => {
  const { checkLeaveCompensations } = useExpenseService();
  const { isGranted } = useAuthorization();
  const { getById: getEmployeeById } = useEmployeeService();

  const validateMaternityLeaveForForm = async (data: LeaveFormData) => {
    if (data.type !== LEAVE_TYPES_ALIASES.MATERNITY_LEAVE) {
      return;
    }
    const employee = await getEmployeeById(data.employeeAlias ?? undefined);
    assertMaternityLeaveAllowed(employee?.officeId, employee?.contractType, data.type, data.subType);
  };

  const singleResourceUrl = '/leaves/:id';
  const {
    update: baseUpdate,
    invalidateCache,
    search: baseSearch,
    ...baseCrudRequests
  } = useCrudService<LeaveDto>({
    singleResourceUrl,
    listResourceUrl: '/leaves',
    useService: usePeopleManagementApi,
    entityName: 'Leave',
  });

  const { sendPostRequest, sendPatchRequest } = usePeopleManagementApi();
  const { sendPostRequest: sendBusinessEnginePostRequest } = useBusinessEngineApi();

  const createEmployeeLeave = async (leave: FormFieldsData) => {
    const data = leave as LeaveFormData;
    await validateMaternityLeaveForForm(data);
    const response = await sendPostRequest('/me/leaves', mapLeaveFormDataToCreateLeaveCommand(data));
    return response.json();
  };

  const create = async (leave: FormFieldsData) => {
    const data = leave as LeaveFormData;
    await validateMaternityLeaveForForm(data);
    return baseCrudRequests.create(mapLeaveFormDataToCreateLeaveCommand(data));
  };

  const canBeExtended = (leave: Data<LeaveDtoWithCompensation>) => {
    const allowedExtensionDate = leave?.getValueBySource('period.allowedExtensionDate');
    const plannedEndDate = leave?.getValueBySource('period.plannedEndDate');
    return !allowedExtensionDate || !plannedEndDate ? false : allowedExtensionDate !== plannedEndDate;
  };

  const update = async (id: StringOrNumber, leave: FormFieldsData) => {
    const leaveFormData = leave as LeaveFormData;
    if (leaveFormData.extendedEndDate || leaveFormData.isExtensionCanceled) {
      const url = `${singleResourceUrl.replace(':id', String(id))}`;
      const response = await sendPatchRequest(url, { extendedEndDate: leaveFormData.extendedEndDate });
      const result = await response.json();

      if (invalidateCache) {
        invalidateCache();
      }

      return result;
    } else {
      await validateMaternityLeaveForForm(leaveFormData);
      return baseUpdate(id, mapLeaveFormDataToUpdateLeaveCommand(leaveFormData));
    }
  };

  const rejectById = async (id: StringOrNumber) => {
    const url = `${singleResourceUrl.replace(':id', String(id))}/reject`;
    await sendBusinessEnginePostRequest(url);
  };

  const getDuration = async (
    type: any,
    startDate: any,
    endDate: any,
    employeeId: any,
  ): Promise<{ duration: number }> => {
    const request = { leaveType: type, startDate, endDate, employeeAlias: employeeId };
    const response = await sendPostRequest('/leaves/duration', request);
    return response.json();
  };

  const searchWithExpensesInfo = async (params: any): Promise<ListData<LeaveDtoWithCompensation>> => {
    try {
      if (!isGranted(FIND_EMPLOYEE_EXPENSES_BY_LEAVE_IDS) || !isGranted(CREATE_EXPENSE)) {
        return baseSearch({ searchRequest: params });
      }

      const result = await baseSearch({ searchRequest: params });

      try {
        const expensesResult = await checkLeaveCompensations(result.result.map(({ id }: any) => id));

        return {
          ...result,
          result: result.result.map((item: { id: any }) => ({
            ...item,
            hasCompensation: !!expensesResult.find(({ leaveId }: any) => String(leaveId) === String(item.id))
              ?.compensationExists,
          })),
        };
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }

      return result;
    } catch {
      return {
        result: [],
      };
    }
  };

  const searchAllWithExpensesInfo = async (params: any): Promise<LeaveDtoWithCompensation[]> => {
    let totalPages = 0;
    let currentPage = 0;
    let result: LeaveDtoWithCompensation[] = [];

    do {
      // eslint-disable-next-line no-await-in-loop
      const response = await searchWithExpensesInfo({
        ...params,
        pageNumber: currentPage,
      });
      // eslint-disable-next-line no-await-in-loop

      currentPage += 1;
      totalPages = response.totalPages || 0;
      result = [
        ...result,
        ...(response.result?.filter((item: LeaveDtoWithCompensation) => !item.hasCompensation) || []),
      ];
    } while (currentPage < totalPages);

    return result;
  };

  const searchUpcomingLeaves = async (userId: string, params: any): Promise<ListData<LeaveDto[] | null>> => {
    if (!userId)
      return {
        result: [],
      };

    const today = new Date();
    const searchParams = {
      ...params,
      filter: {
        ...params.filter,
        'employee.alias': { eq: userId },
        startDate: { ge: today.toISOString().split('T')[0] },
        status: {
          in: [LEAVE_STATUSES_ALIASES.APPROVED, LEAVE_STATUSES_ALIASES.IN_APPROVAL, LEAVE_STATUSES_ALIASES.REGISTERED],
        },
      },
      sort: { startDate: 'ASC' },
    };

    return baseSearch({ searchRequest: searchParams });
  };

  const getByIdWithExpensesInfo = async (id: string): Promise<LeaveDtoWithCompensation> => {
    if (!isGranted(FIND_EMPLOYEE_EXPENSES_BY_LEAVE_IDS) || !isGranted(CREATE_EXPENSE)) {
      return baseCrudRequests.getById(id);
    }

    const result = await baseCrudRequests.getById(id);
    try {
      const expensesResult = await checkLeaveCompensations([id]);

      return {
        ...result,
        hasCompensation: !!expensesResult[0].compensationExists,
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }

    return result;
  };

  const getValidationSchema = () => Promise.resolve(leaveValidation);

  const getFiltersValidationSchema = () => Promise.resolve(leaveFilterValidation);

  const getOneStopValidationSchema = () => Promise.resolve(oneStopLeaveValidation);

  return {
    getValidationSchema,
    getFiltersValidationSchema,
    getOneStopValidationSchema,
    createEmployeeLeave,
    canBeExtended,
    create,
    getById: baseCrudRequests.getById,
    searchAll: baseCrudRequests.searchAll,
    getPermissions: baseCrudRequests.getPermissions,
    getPermissionsEntity: baseCrudRequests.getPermissionsEntity,
    update,
    getDuration,
    rejectById,
    searchWithExpensesInfo,
    getByIdWithExpensesInfo,
    searchAllWithExpensesInfo,
    searchUpcomingLeaves,
  };
};

export default useLeaveService;
